import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";

import Alert from "veneer/Alert";
import Box from "veneer/Box";
import Button from "veneer/Button";
import DateTimePicker from "veneer/DateTimePicker";
import Stack from "veneer/Stack";
import TextInput from "veneer/TextInput";

import LogsConstants from "cortex/ui/src/fleet/constant/logs";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import {
  applicationsSelector,
  applicationsStoresLoadedSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import { GET_LOGS_DEVICE } from "cortex/ui/src/redux-store/devices/actions";
import Notification from "cortex/ui/src/ui/notification";
import type { ResponseLike } from "cortex/ui/src/util/response";
import { getErrorMessage } from "cortex/ui/src/util/response";
import { Panel } from "cortex/ui/src/view/layout";

import LogsTable from "./component/LogsTable";
import { Device, DeviceLog } from "./model";

declare function require(moduleName: string): unknown;
type SemverValid = (version: string | null | undefined) => string | null;
type SemverGte = (version: string, other: string) => boolean;
const semverValid = require("semver/functions/valid") as SemverValid;
const semverGte = require("semver/functions/gte") as SemverGte;

interface DeviceLogsPageProps {
  device: Device;
  storesLoaded: boolean;
}

type TagOption = {
  name: string;
  label: string;
  placeholder: string;
  value: string;
};

type DeviceLogsResponse = {
  data?: {
    data?: ReadonlyArray<DeviceLog>;
  };
};

const oldLogsRangeSeconds = 60 * 60; // 1 hour in seconds

function orderLines(lines: ReadonlyArray<DeviceLog> = []): DeviceLog[] {
  const sorted = [...lines].sort(
    (a, b) => Date.parse(a.timestamp) - Date.parse(b.timestamp),
  );

  const seen = new Set<string>();

  return sorted.filter((line) => {
    const key = JSON.stringify(line);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getDurationError(minutes: number, maxMinutes: number): string {
  if (Number.isNaN(minutes) || minutes < 1 || minutes > maxMinutes) {
    return `Please enter a valid number between 1 and ${maxMinutes}.`;
  }
  return "";
}

export default function DeviceLogs({
  device,
  storesLoaded: parentStoresLoaded,
}: DeviceLogsPageProps) {
  const dispatch = useAppDispatch();
  const applications = useAppSelector(applicationsSelector);
  const applicationsStoresLoaded = useAppSelector(
    applicationsStoresLoadedSelector,
  );
  const applicationList = useMemo(
    () => Object.values(applications),
    [applications],
  );
  const [error, setError] = useState("");
  const level = "all";
  const [lines, setLines] = useState<ReadonlyArray<DeviceLog>>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [specificDateSet, setSpecificDateSet] = useState(false);
  const [datepickerDuration, setDatepickerDuration] = useState(
    oldLogsRangeSeconds / 60,
  );
  const [datepickerStart, setDatepickerStart] = useState(
    (moment().unix() - oldLogsRangeSeconds) * 1000,
  );
  const storesLoaded = useMemo(
    () => parentStoresLoaded && applicationsStoresLoaded,
    [parentStoresLoaded, applicationsStoresLoaded],
  );

  const allowExtendedLogs = useMemo(() => {
    return Boolean(
      semverValid(device.current_version) &&
        semverGte(
          device.current_version,
          LogsConstants.MIN_VER_FOR_EXTENDED_LOGS,
        ),
    );
  }, [device.current_version]);

  const maxMinutes = allowExtendedLogs ? 240 : 60;

  const datepickerDurationError = useMemo(() => {
    return getDurationError(Number(datepickerDuration), maxMinutes);
  }, [datepickerDuration, maxMinutes]);

  const filteredLines = useMemo(() => {
    return lines.map((line, index) => ({
      ...line,
      id: index + 1,
    }));
  }, [lines]);

  const commonErrors = useMemo(() => {
    const counts = new Map<string, number>();

    for (const line of lines) {
      if (
        (line.level === "warn" || line.level === "error") &&
        line.tag?.startsWith("app.")
      ) {
        counts.set(line.message, (counts.get(line.message) ?? 0) + 1);
      }
    }

    return Array.from(counts.entries()).filter(([, count]) => count > 1);
  }, [lines]);

  const tagOptions: TagOption[] = useMemo(() => {
    const uniqueTags = Array.from(
      new Set(lines.map((line) => line.tag).filter(Boolean)),
    );

    return uniqueTags.map((tag) => ({
      name: tag,
      label: tag,
      placeholder: tag,
      value: tag,
    }));
  }, [lines]);

  const startSeconds = Math.floor(datepickerStart / 1000);
  const endSeconds = startSeconds + Number(datepickerDuration) * 60;
  const tableData = refreshing ? [] : filteredLines;
  const dateTimeFormat = "MMM Do, YYYY hh:mm a";

  const logFileName = useMemo(() => {
    return `cortex-device-${device.device_id}-${startSeconds}-${endSeconds}.log`;
  }, [device.device_id, startSeconds, endSeconds]);

  const fetchDeviceLogs = useCallback(
    ({
      deviceId,
      level,
      start,
      end,
    }: {
      deviceId: string;
      level?: string;
      start: number;
      end: number;
    }) => {
      return createPromiseApiAction(dispatch, GET_LOGS_DEVICE, [
        deviceId,
        level,
        start,
        end,
      ]);
    },
    [dispatch],
  );

  const retrieveLogs = useCallback(
    async (start: number, end: number) => {
      setRefreshing(true);
      setError("");

      try {
        const res = (await fetchDeviceLogs({
          deviceId: String(device.id),
          level,
          start,
          end,
        })) as DeviceLogsResponse;

        const data = res?.data?.data ?? [];
        const orderedLines = orderLines(data);
        setLines(orderedLines);
      } catch (e) {
        setError(
          `Failed to retrieve device logs. Your device might be offline or not supporting this feature yet. Server reply: ${getErrorMessage(
            e as Error | ResponseLike | null,
          )}`,
        );
      } finally {
        setRefreshing(false);
      }
    },
    [device.id, fetchDeviceLogs],
  );

  const handleDatepickerChange = useCallback((d: moment.Moment | null) => {
    if (!d || typeof d.unix !== "function") {
      return;
    }

    setDatepickerStart(d.unix() * 1000);
    setSpecificDateSet(true);
  }, []);

  const handleDurationChange = useCallback(
    (value: string | null) => {
      const minutes = Number(value);
      setDatepickerDuration(minutes);

      if (
        !specificDateSet &&
        !Number.isNaN(minutes) &&
        minutes >= 1 &&
        minutes <= maxMinutes
      ) {
        setDatepickerStart(moment().subtract(minutes, "minutes").unix() * 1000);
      }
    },
    [specificDateSet, maxMinutes],
  );

  const handleDownloadLogs = useCallback(() => {
    if (filteredLines.length === 0) {
      return;
    }

    const blob = new Blob(
      [filteredLines.map((obj) => JSON.stringify(obj)).join("\n")],
      { type: "application/json" },
    );

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = logFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }, [filteredLines, logFileName]);

  const clickedFetchLogs = useCallback(() => {
    if (datepickerDurationError) {
      return;
    }

    retrieveLogs(startSeconds, endSeconds);
  }, [datepickerDurationError, retrieveLogs, startSeconds, endSeconds]);

  if (!storesLoaded || !device) {
    return null;
  }

  const durationHelpText = allowExtendedLogs
    ? "Must be between 1-240 minutes"
    : "Must be between 1-60 minutes";

  return (
    <Panel gap={2}>
      <Stack alignItems="start" direction="row" spacing={2}>
        <Stack alignItems="start" direction="row" spacing={2}>
          <Stack>
            <DateTimePicker
              format="MMM Do, YYYY hh:mm A"
              label="Start Date and Time"
              maxDate={moment()}
              minDate={moment().subtract(7, "days")}
              onChange={handleDatepickerChange}
              size="small"
              value={moment(datepickerStart)}
            />
          </Stack>
          <Stack>
            <TextInput
              endAdornment="min"
              error={Boolean(datepickerDurationError)}
              helperText={datepickerDurationError || durationHelpText}
              label="Log Duration (minutes)"
              onChange={handleDurationChange}
              value={String(datepickerDuration)}
            />
          </Stack>
        </Stack>
        <Button disabled={refreshing} icon="refresh" onClick={clickedFetchLogs}>
          Retrieve
        </Button>
        <Button
          disabled={filteredLines.length === 0 || refreshing}
          icon="download"
          onClick={handleDownloadLogs}
          variant="outlined"
        >
          Download
        </Button>
      </Stack>
      <Alert severity="info">
        With current date range settings, you will retrieve logs from{" "}
        <strong>{moment(datepickerStart).format(dateTimeFormat)}</strong> to{" "}
        <strong>
          {moment(
            datepickerStart + Number(datepickerDuration) * 60 * 1000,
          ).format(dateTimeFormat)}
        </strong>
        .
      </Alert>
      {commonErrors.length > 0 && (
        <>
          <h4>Common Messages</h4>
          <ul>
            {commonErrors.map(([message, count]) => (
              <li key={message}>
                {message} ({count} times)
              </li>
            ))}
          </ul>
        </>
      )}
      <Notification error={error} />
      <Box mx={-2}>
        <LogsTable
          applications={applicationList}
          data={tableData}
          refreshing={refreshing}
          tagOptions={tagOptions}
        />
      </Box>
    </Panel>
  );
}
